import React, { useEffect, useMemo, useState } from 'react';
import { useAlbumStore, useProfileStore } from '../../stores/useDataStore';
import { getMediaUrl } from '../../utils/imageUtils';
import ImageModal from '../common/ImageModal';

const UserPhotoAlbum = ({
  username,
  isOwner,
  selectMode = false,
  onSelect,
  showAlbumControls = true,
  showCollections = true,
  initialAlbumType = 'custom',
  allowedAlbumTypes = null,
}) => {
  const collections = useAlbumStore((state) => state.collections);
  const isCollectionsLoading = useAlbumStore((state) => state.isLoading);
  const fetchAlbumCollections = useAlbumStore((state) => state.fetchAlbumCollections);
  const fetchAlbumPhotos = useAlbumStore((state) => state.fetchAlbumPhotos);
  const updatePhotoVisibility = useAlbumStore((state) => state.updatePhotoVisibility);
  const updateAlbumVisibility = useAlbumStore((state) => state.updateAlbumVisibility);
  const removePhoto = useProfileStore((state) => state.removePhoto);

  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingPhotoId, setUpdatingPhotoId] = useState(null);
  const [selectedAlbumType, setSelectedAlbumType] = useState(showCollections ? null : initialAlbumType);
  const [selectedAlbumId, setSelectedAlbumId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);

  useEffect(() => {
    if (!username || !showCollections) return;
    fetchAlbumCollections(username);
  }, [username, fetchAlbumCollections, showCollections]);

  useEffect(() => {
    if (showCollections) return;
    setSelectedAlbumType(initialAlbumType);
    setSelectedAlbumId(null);
  }, [showCollections, initialAlbumType, username]);

  useEffect(() => {
    if (!username || !selectedAlbumType) return;

    let isMounted = true;
    setIsLoading(true);

    fetchAlbumPhotos(username, selectedAlbumType, selectedAlbumId)
      .then((data) => {
        if (!isMounted) return;
        setAlbumPhotos(Array.isArray(data?.photos) ? data.photos : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setAlbumPhotos([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [username, selectedAlbumType, selectedAlbumId, fetchAlbumPhotos]);

  const combinedPhotos = useMemo(() => {
    return (albumPhotos || [])
      .map((photo) => ({
        id: photo._id || photo.publicId || photo.url,
        url: photo.url,
        publicId: photo.publicId,
        createdAt: photo.createdAt,
        isPublic: !!photo.isPublic,
        source: selectedAlbumType || 'album',
      }))
      .sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
  }, [albumPhotos, selectedAlbumType]);

  const handleSelect = (photo) => {
    if (!selectMode || typeof onSelect !== 'function') return;
    onSelect(photo);
  };

  const handleOpenLightbox = (photo) => {
    const index = combinedPhotos.findIndex((item) => item.id === photo.id);
    if (index >= 0) {
      setCurrentIndex(index);
    }
  };

  const handleRemove = async (photo) => {
    if (!photo?.publicId) return;
    const result = await removePhoto(username, photo.publicId);
    if (result?.success) {
      setAlbumPhotos((prev) => prev.filter((item) => item.publicId !== photo.publicId));
    }
  };

  const handleRemoveByPublicId = async (publicId) => {
    if (!publicId) return;
    const result = await removePhoto(username, publicId);
    if (result?.success) {
      setAlbumPhotos((prev) => prev.filter((item) => item.publicId !== publicId));
    }
  };

  const handleToggleVisibility = async (photo) => {
    if (!photo.publicId) return;
    setUpdatingPhotoId(photo.publicId);
    const result = await updatePhotoVisibility(photo.publicId, !photo.isPublic);
    if (result?.success) {
      setAlbumPhotos((prev) =>
        prev.map((item) =>
          item.publicId === photo.publicId ? { ...item, isPublic: !photo.isPublic } : item
        )
      );
    }
    setUpdatingPhotoId(null);
  };

  const handleAlbumVisibility = async (isPublic) => {
    if (!selectedAlbumId) return;
    const result = await updateAlbumVisibility(selectedAlbumId, isPublic);
    if (result?.success) {
      setAlbumPhotos((prev) => prev.map((item) => ({ ...item, isPublic })));
    }
  };

  const handleSelectAlbum = (collection) => {
    setSelectedAlbumType(collection.albumType);
    setSelectedAlbumId(collection.id || null);
  };

  const handleBackToCollections = () => {
    setSelectedAlbumType(null);
    setSelectedAlbumId(null);
    setAlbumPhotos([]);
    setCurrentIndex(null);
  };

  if (showCollections && !selectedAlbumType) {
    if (isCollectionsLoading) {
      return <div className="text-center text-gray-500 dark:text-gray-400 py-10">Cargando album...</div>;
    }

    if (!collections || collections.length === 0) {
      return (
        <div className="text-center text-gray-500 dark:text-gray-400 py-10">
          <i className="fas fa-camera-retro text-4xl mb-4 text-gray-400 dark:text-gray-500"></i>
          <p>No hay colecciones disponibles.</p>
        </div>
      );
    }

    const visibleCollections = (collections || [])
      .filter((collection) => collection?.albumType !== 'posts')
      .filter((collection) => {
        if (!Array.isArray(allowedAlbumTypes) || allowedAlbumTypes.length === 0) return true;
        return allowedAlbumTypes.includes(collection?.albumType);
      });

    if (!visibleCollections.length) {
      return (
        <div className="text-center text-gray-500 dark:text-gray-400 py-10">
          <i className="fas fa-camera-retro text-4xl mb-4 text-gray-400 dark:text-gray-500"></i>
          <p>No hay colecciones disponibles.</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {visibleCollections.map((collection) => (
          <button
            key={`${collection.albumType}-${collection.id || collection.name}`}
            type="button"
            onClick={() => handleSelectAlbum(collection)}
            className="text-left group rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700 hover:border-blue-400 transition-colors bg-white dark:bg-slate-900"
          >
            <div className="h-32 bg-gray-100 dark:bg-slate-800">
              {collection.coverUrl ? (
                <img
                  src={getMediaUrl(collection.coverUrl)}
                  alt={collection.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                  <i className="fas fa-folder-open text-2xl"></i>
                </div>
              )}
            </div>
            <div className="p-3">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{collection.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{collection.count || 0} items</p>
            </div>
          </button>
        ))}
      </div>
    );
  }

  if (isLoading) {
    return <div className="text-center text-gray-500 dark:text-gray-400 py-10">Cargando album...</div>;
  }

  if (!combinedPhotos.length) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-10">
        <i className="fas fa-camera-retro text-4xl mb-4 text-gray-400 dark:text-gray-500"></i>
        <p>No hay fotos disponibles.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        {showCollections ? (
          <button
            type="button"
            onClick={handleBackToCollections}
            className="text-xs font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver a colecciones
          </button>
        ) : (
          <div />
        )}

        {isOwner && showAlbumControls && selectedAlbumId && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Privacidad del album</span>
            <button
              type="button"
              onClick={() => handleAlbumVisibility(true)}
              className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
            >
              Todo publico
            </button>
            <button
              type="button"
              onClick={() => handleAlbumVisibility(false)}
              className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-slate-600"
            >
              Todo privado
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {combinedPhotos.map((photo) => (
          <div
            key={photo.id}
            className="group relative w-full h-36 overflow-hidden rounded-lg cursor-pointer"
            onClick={() => (selectMode ? handleSelect(photo) : handleOpenLightbox(photo))}
          >
            <img
              src={getMediaUrl(photo.url)}
              alt="Foto del album"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />

            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <i className="fas fa-eye text-white text-2xl view-icon"></i>
            </div>

            {photo.source === 'album' && isOwner && showAlbumControls && (
              <div className="absolute top-2 left-2 flex items-center gap-2 bg-white/80 px-2 py-1 rounded-full text-[10px] text-gray-700">
                <span>{photo.isPublic ? 'Publico' : 'Privado'}</span>
              </div>
            )}

            {selectMode && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <i className="fas fa-check-circle text-white text-3xl"></i>
              </div>
            )}

            {photo.source !== 'posts' && isOwner && showAlbumControls && !selectMode && (
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleVisibility(photo);
                  }}
                  disabled={updatingPhotoId === photo.publicId}
                  className="bg-black/60 text-white w-7 h-7 rounded-full flex items-center justify-center"
                  title={photo.isPublic ? 'Hacer privado' : 'Hacer publico'}
                >
                  <i className={`fas ${photo.isPublic ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemove(photo);
                  }}
                  className="bg-black/60 text-white w-7 h-7 rounded-full flex items-center justify-center"
                  title="Eliminar"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {currentIndex !== null && (
        <ImageModal
          images={combinedPhotos}
          currentIndex={currentIndex}
          onClose={() => setCurrentIndex(null)}
          onNext={() => setCurrentIndex((prev) => (prev + 1) % combinedPhotos.length)}
          onPrev={() => setCurrentIndex((prev) => (prev - 1 + combinedPhotos.length) % combinedPhotos.length)}
          onRemove={handleRemoveByPublicId}
          isMyProfile={isOwner}
        />
      )}
    </div>
  );
};

export default UserPhotoAlbum;
